import * as React from 'react';
import { Doughnut } from 'react-chartjs-2';
import colors from 'src/integration/styles/colors';
import { ChartOptions } from 'chart.js';
import { RevenueDonutDataItem } from 'src/integration/data/RevenueDonutData';
import { getChartTypographyScale, scaleChartSize } from 'src/integration/settings/graphSettings';
import styled from 'styled-components';

interface ComponentProps {
  data: RevenueDonutDataItem[];
}

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${colors.grey1};
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const RevenueDonutChart = ({ data }: ComponentProps) => {
  const chartTypographyScale = getChartTypographyScale();

  // Check if all amounts are 0 or if there's no data
  const totalAmount = data.reduce((sum, item) => sum + (item.amount || 0), 0);
  const hasNoData = !data || data.length === 0 || totalAmount === 0;

  // Return empty state early if no data
  if (hasNoData) {
    return (
      <EmptyStateContainer>
        No revenue data available for the selected period
      </EmptyStateContainer>
    );
  }

  // Filter out items with zero or null amounts to avoid showing 0.00% segments
  const filteredData = data.filter(item => item.amount && item.amount > 0);

  // Map each revenue type to its specific color
  const colorMap: Record<string, string> = {
    'aFRR revenue': colors.chart.carminePink,
    'mFRR revenue': colors.chart.oldMauve,
    'FCR revenue': colors.text.warning,
    'Intraday revenue': colors.chart.blackOlive,
    'Day ahead revenue': colors.chart.richBrilliantLavender
  };

  const chartData = {
    labels: filteredData.map(item => item.title),
    datasets: [{
      data: filteredData.map(item => item.amount),
      backgroundColor: filteredData.map(item => colorMap[item.title] || colors.grey1),
      borderWidth: 0
    }]
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: scaleChartSize(30, chartTypographyScale),
          boxHeight: scaleChartSize(20, chartTypographyScale),
          padding: scaleChartSize(10, chartTypographyScale),
          font: {
            size: scaleChartSize(14, chartTypographyScale),
          },
          generateLabels: (chart) => {
            // Show all revenue types in legend, not just those with data
            const allRevenueTypes = [
              'aFRR revenue',
              'mFRR revenue',
              'FCR revenue',
              'Intraday revenue',
              'Day ahead revenue'
            ];

            return allRevenueTypes.map((label, index) => {
              const dataItem = data.find(item => item.title === label);
              const hasValue = dataItem && dataItem.amount > 0;

              return {
                text: label,
                fillStyle: colorMap[label] || colors.grey1,
                hidden: false,
                index: index,
                strokeStyle: colorMap[label] || colors.grey1,
                lineWidth: 0,
                fontColor: hasValue ? colors.primaryDarkJungleGreen : colors.grey2,
              };
            });
          },
        },
      },
      datalabels: {
        formatter: (value: number, ctx: any) => {
          const total = ctx.dataset.data.reduce((acc: number, curr: number) => acc + curr, 0);
          if (!total) {
            return '';
          }
          const percentage = (value / total) * 100;
          return `${percentage.toFixed(2)}%`;
        },
        color: 'white',
        font: {
          weight: 700,
          size: scaleChartSize(12, chartTypographyScale),
        },
      }
    },
  }

  return (
    <Doughnut
      data={chartData}
      options={options}
    />
  )
}

export default RevenueDonutChart;
